import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { ButtonGroup } from '@atlaskit/button';
import { Stack, Text } from '@atlaskit/primitives';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

const i18n = defineMessages({
	frictionModalTitle: {
		id: 'team-admin-controls.friction-modal.title',
		defaultMessage: 'Some teams may lose access',
		description: 'Title for the friction modal',
	},
	noTeamsFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.body',
		defaultMessage:
			'You are turning off the ability to manage permissions with teams. may cause unintended loss of access across your site.',
		description: 'Body text for the friction modal',
	},
	noTeamsFrictionModalBodyAdditional: {
		id: 'team-admin-controls.friction-modal.body-additional',
		defaultMessage: '<b>You can’t undo this.</b>',
		description: 'Additional body text for the friction modal',
	},
	verifiedTeamsFrictionModalBody: {
		id: 'team-admin-controls.friction-modal.body',
		defaultMessage:
			'Atlassian team is a collection of users that are part of your real-world team. Limiting the use teams to <b>only verified</b> may cause unintended loss of access across your site.',
		description: 'Body text for the friction modal',
	},
	verifiedTeamsFrictionModalBodyAdditional: {
		id: 'team-admin-controls.friction-modal.body-additional',
		defaultMessage: '<b>You can restore this later.</b>',
		description: 'Additional body text for the friction modal',
	},
	frictionModalConfirmButton: {
		id: 'team-admin-controls.friction-modal.confirm-button',
		defaultMessage: 'Confirm',
		description: 'Confirm button text for the friction modal',
	},
	frictionModalCancelButton: {
		id: 'team-admin-controls.friction-modal.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for the friction modal',
	},
});

export const FrictionModal: FC<{
	title?: string;
	width?: string | number;
	onConfirm: () => void;
	onClose: () => void;
	teamAccessType: 'verified' | 'none';
}> = ({ title, onConfirm, onClose, width = 'small', teamAccessType }) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const bodyText =
		teamAccessType === 'verified'
			? formatMessage(i18n.verifiedTeamsFrictionModalBody, {
					b: (chunks: React.ReactNode) => <b>{chunks}</b>,
				})
			: formatMessage(i18n.noTeamsFrictionModalBody);
	const bodyAdditionalText =
		teamAccessType === 'verified'
			? formatMessage(i18n.verifiedTeamsFrictionModalBodyAdditional, {
					b: (chunks: React.ReactNode) => <b>{chunks}</b>,
				})
			: formatMessage(i18n.noTeamsFrictionModalBodyAdditional, {
					b: (chunks: React.ReactNode) => <b>{chunks}</b>,
				});

	return (
		<Modal width={width} onClose={onClose}>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="warning">
					{title || formatMessage(i18n.frictionModalTitle)}
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Text> {bodyText}</Text>
					<Text>{bodyAdditionalText}</Text>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<ButtonGroup>
					<Button appearance="warning" onClick={onConfirm}>
						{formatMessage(i18n.frictionModalConfirmButton)}
					</Button>
					<Button appearance="subtle" onClick={onClose}>
						{formatMessage(i18n.frictionModalCancelButton)}
					</Button>
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};
